import { queryClient } from 'shared/api/core/tanstack-query-config'
import {
	migrateLegacyAuth,
	readCachedSession,
	removeCachedSession,
} from './storage'
import {
	type AuthMode,
	type CachedSession,
	type User,
	useAuthStore,
} from 'shared/store/auth'

interface AuthResult {
	token: string
	expiresAt: number
	user: User
}

export function bootstrapCachedAuth(
	mode: AuthMode,
	currentTelegramId?: number,
	isDevelopment = import.meta.env.DEV,
): boolean {
	migrateLegacyAuth()
	// Telegram capabilities come from the backend environment and can change
	// without changing the Telegram identity. Do not hydrate a production
	// Telegram session with potentially stale permissions; the AuthGate will
	// immediately exchange the current initData and persist the fresh session.
	if (!isDevelopment && mode === 'telegram') {
		const cachedSession = readCachedSession(
			mode,
			currentTelegramId,
			Date.now(),
			isDevelopment,
		)
		if (!cachedSession?.isDevOverride) {
			clearAuthSession(mode)
			return false
		}
	}
	const session = readCachedSession(
		mode,
		currentTelegramId,
		Date.now(),
		isDevelopment,
	)
	if (!session) {
		clearAuthSession(mode)
		return false
	}
	useAuthStore.getState().setSession(session)
	return true
}

function installSession(session: CachedSession): void {
	const previous = useAuthStore.getState()
	const identityChanged =
		previous.status === 'authenticated' &&
		(previous.mode !== session.mode ||
			previous.user._id !== session.user._id ||
			previous.telegramId !== (session.telegramId ?? null))
	if (identityChanged) queryClient.clear()
	previous.setSession(session)
}

export function installTelegramSession(
	result: AuthResult,
	currentTelegramId: number,
): void {
	if (result.user.telegramId !== currentTelegramId) {
		throw new Error('Telegram identity changed during authentication')
	}
	installSession({
		version: 3,
		mode: 'telegram',
		telegramId: currentTelegramId,
		...result,
	})
}

export function installBrowserAdminSession(result: AuthResult): void {
	installSession({ version: 3, mode: 'browser-admin', ...result })
}

export function installDevOverrideSession(result: AuthResult): void {
	installSession({
		version: 3,
		mode: 'telegram',
		telegramId: result.user.telegramId,
		isDevOverride: true,
		...result,
	})
}

export function clearAuthSession(mode?: AuthMode, error?: string): void {
	queryClient.clear()
	const state = useAuthStore.getState()
	if (mode && state.mode !== mode) removeCachedSession(mode)
	state.clearSession()
	if (error) useAuthStore.getState().setError(error)
}
